/**
 * blockMutations.ts
 *
 * The ONE place that writes block changes to IndexedDB. Every function here does
 * the same two things in a SINGLE atomic transaction (both succeed, or neither):
 *   1. write the block row(s) in the `blocks` store, and
 *   2. add a row to the `syncQueue` store, which SyncWatcher later sends to the
 *      server.
 * Doing both together means IndexedDB and the pending-sync list can never drift
 * apart — you can't end up with a saved block that was never queued, or vice versa.
 *
 * What if IndexedDB can't be used? (Safari private mode, storage disabled, disk
 * full.) Each mutation falls back to calling the API directly, in two ways:
 *   1. Before writing — isIDBAvailable() is already false, so skip IndexedDB and
 *      call the API now, so the change is never silently lost.
 *   2. While writing — the write throws a quota error mid-session; the catch
 *      block marks IndexedDB "quota_full" (handleQuotaError) and calls the API.
 *
 * Two functions are the exception: loadBlocksMutation and upsertBlocksMutation
 * just fill IndexedDB with data that already came from the API, so they add no
 * syncQueue row and need no API fallback.
 */

import { readCurrentUserStamp } from "@/lib/currentUserStamp";
import { appDB, emitIDBChange } from "../db/client";
import { toBlockRecord } from "../stores/blocksStore";
import { toDataSourceRecord } from "../stores/dataSourcesStore";
import { countPendingOps, getPendingBlockIds } from "../stores/syncQueueStore";
import { setPendingCount } from "../stores/syncMetaStore";
import { touchPageCache, runEviction, recordLocalPageEdit } from "../eviction";
import { isIDBAvailable, setIDBStatus, isQuotaError } from "../idbStatus";
import { cascadeDatasourceDeleteInIDB } from "./cascadeDeleteMutations";
import { markDatasourceCreating, clearDatasourceCreating } from "@/lib/datasourceTombstones";
import { serializeViewDatabaseForAPI, serializeDataSourceForAPI } from "@/lib/collectionViewTemplate";
import { seedMissingStatusGroups } from "@/lib/statusGroups";
import { resolveTemplateDuplicator, type DuplicatorUser } from "@/utils/templateDuplicator";
import { postWithAuth, deleteWithAuth } from "@/lib/api-helpers";
import { ObjectId } from "bson";
import type { Block, BlockChangesPayload, ParentTable } from "@/types/block";
import type { DatabaseSource } from "@/types/board";
import type { PageCreatePayload, BlockDeletePayload, PageMovePayload, UpdatePropertyValuePayload, PageSettingsUpdatePayload } from "../db/schema";

// ─────────────────────────────────────────────────────────────────
// INTERNAL HELPERS
// ─────────────────────────────────────────────────────────────────

/**
 * Marks IDB as quota_full and logs a warning.
 * Called from every mutation's catch block when a mid-session quota error is detected.
 */
function handleQuotaError(mutationName: string): void {
  setIDBStatus("quota_full");
  console.warn(
    `%c[${mutationName}] IDB quota exceeded mid-session — falling back to direct API for this op`,
    "color: #fb923c; font-weight: bold;"
  );
}

// ─────────────────────────────────────────────────────────────────
// UPDATE PROPERTY VALUE
// Specifically for database properties via the specialized API.
// ─────────────────────────────────────────────────────────────────

export interface UpdatePropertyValueOptions {
  dataSourceId: string;
  blockId: string;
  propertyId: string;
  value: any;
  workspaceName: string;
  workspaceId: string;
}

/**
 * Updates a specific database property on a block locally and enqueues
 * a specialized SYNC operation for /api/database/update-property-value.
 */
export async function updatePropertyValueMutation(
  options: UpdatePropertyValueOptions
): Promise<void> {
  const { dataSourceId, blockId, propertyId, value, workspaceName, workspaceId } = options;

  const payload: UpdatePropertyValuePayload = {
    dataSourceId,
    blockId,
    propertyId,
    value,
    workspaceName,
    workspaceId,
  };

  // ── Direct API path (IDB unavailable or quota full) ──────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/update-property-value", payload);
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      // 1. Update the property in the local block record
      const existing = await appDB.blocks.get(blockId);
      if (existing && existing.value) {
        const updatedProps = {
          ...(existing.value.databaseProperties || {}),
          [propertyId]: value,
        };

        await appDB.blocks.put({
          ...existing,
          value: {
            ...existing.value,
            databaseProperties: updatedProps,
          },
          // Optimistic edit stamp; the server's copy replaces it on refetch.
          updatedAt: new Date().toISOString(),
          lastEditedBy: readCurrentUserStamp() ?? existing.lastEditedBy,
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      // 2. Enqueue the specialized sync operation
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "UPDATE_PROPERTY_VALUE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    // Same stamp the page header reads, so the row's "Last edited" columns move at once.
    void recordLocalPageEdit(blockId, Date.now(), readCurrentUserStamp());

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updatePropertyValueMutation");
      await postWithAuth("/api/database/update-property-value", payload);
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// ADD BLOCK
// Replaces: blockContext.addBlock()
// ─────────────────────────────────────────────────────────────────

export interface AddBlockOptions {
  block: Block;
  previousBlockId: string | null;          // for ordering in parent (null = top)
  workspaceId: string;
  parentId: string | null;
}

/**
 * Adds a new block locally and enqueues a CREATE operation for the watcher.
 * UI updates instantly. MongoDB updated asynchronously.
 */
export async function addBlockMutation(options: AddBlockOptions): Promise<void> {
  const { block, previousBlockId, workspaceId, parentId } = options;

  const payload: BlockChangesPayload = {
    noteId: block._id,
    workspaceId,
    parentId,
    creates: [
      {
        blockId: block._id,
        previousBlockId,
        content: block.value,
      },
    ],
    updates: [],
    deletes: [],
    move: null,
  };

  // API shape for batch-update
  const apiPayload = {
    parentId,
    workspaceId,
    blocks: [{ _id: block._id, content: block.value }],
  };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/note/block/batch-update", apiPayload);
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      // 1. Write block to IDB
      await appDB.blocks.put(toBlockRecord(block, 0));

      // 2. Enqueue sync operation
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "BLOCK_CHANGES",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: block._id,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("addBlockMutation");
      await postWithAuth("/api/note/block/batch-update", apiPayload);
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// CREATE COLLECTION (Board/List)
// Creates a board block AND its datasource atomically.
// ─────────────────────────────────────────────────────────────────

export interface CreateCollectionOptions {
  block: Block;
  dataSource: DatabaseSource;
  previousBlockId: string | null;
  workspaceId: string;
  parentId: string;
  workareaId: string | null;
  parentTable?: ParentTable;
  sidebarSection?: "public" | "restricted" | "private";
}

/**
 * Atomic local-first creation for database-style blocks (Board, List, etc).
 * Enqueues a single BLOCK_CREATE op containing both the block and the datasource detail.
 */
export async function createCollectionMutation(options: CreateCollectionOptions): Promise<void> {
  const { block, dataSource, previousBlockId, workspaceId, parentId, workareaId, parentTable = "page", sidebarSection } = options;

  // Synchronous, before any await: shields this datasource's 404s from being
  // read as "deleted" while the creation is in flight.
  markDatasourceCreating(String(dataSource._id));

  // The server seeds status groups on write; do the same here so the IDB copy,
  // the request body and anything the caller still holds all agree. Without it
  // a template renders ungrouped until the next refetch.
  dataSource.properties = seedMissingStatusGroups(dataSource.properties) ?? dataSource.properties;

  const payload: PageCreatePayload = {
    parentId,
    workspaceId,
    workareaId,
    parentTable,
    blocks: [
      {
        _id: block._id,
        blockType: "collection_view",
        value: serializeViewDatabaseForAPI(block.value as any),
        insertAfterBlockID: previousBlockId,
        ...(sidebarSection ? { sidebarSection } : {}),
      },
    ],
    dataSourceDetail: serializeDataSourceForAPI(dataSource),
  };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/note/block/batch-create", payload);
    clearDatasourceCreating(String(dataSource._id));
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.dataSources, appDB.syncQueue], async () => {
      // 1. Write the collection_view block (isSync: 0)
      await appDB.blocks.put(toBlockRecord(block, 0));

      // 2. Write the associated dataSource (isSync: 0)
      await appDB.dataSources.put(toDataSourceRecord(dataSource, workspaceId, 0));

      // 3. Update parent blockIds locally
      const parentBlock = await appDB.blocks.get(parentId);
      if (parentBlock) {
        const currentIds = parentBlock.blockIds || [];
        let newIds = currentIds.filter(id => id !== block._id);

        if (!previousBlockId) {
          newIds = [...newIds, block._id];
        } else {
          const idx = newIds.indexOf(previousBlockId);
          if (idx === -1) {
            newIds.push(block._id);
          } else {
            newIds.splice(idx + 1, 0, block._id);
          }
        }

        await appDB.blocks.put({
          ...parentBlock,
          blockIds: newIds,
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      // 4. Enqueue the combined creation operation
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "BLOCK_CREATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: block._id,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("createCollectionMutation");
      await postWithAuth("/api/note/block/batch-create", payload);
      clearDatasourceCreating(String(dataSource._id));
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE BLOCK CONTENT
// Replaces: blockContext.updateBlock() for content updates
// ─────────────────────────────────────────────────────────────────

export interface UpdateBlockContentOptions {
  blockId: string;
  noteId: string;                           // the page this block belongs to
  data: any;                                // full ProseMirror node JSON
  workspaceId: string;
  parentId: string | null;
}

/**
 * Updates a content block locally and enqueues an UPDATE operation.
 */
export async function updateBlockContentMutation(
  options: UpdateBlockContentOptions
): Promise<void> {
  const { blockId, noteId, data, workspaceId, parentId } = options;

  // ── Direct API path ───────────────────────────────────────────────────────
  // No IDB to read the block's own parentId from. The caller now passes the
  // block's OWN parentId (from context) — correct for nested blocks — so prefer
  // it; fall back to the page (noteId) only if it's missing. Never the page's
  // parent (which would reparent the block server-side via $set).
  if (!isIDBAvailable()) {
    await postWithAuth("/api/note/block/batch-update", {
      parentId: parentId ?? noteId,
      workspaceId,
      blocks: [{ _id: blockId, content: data }],
    });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      // 1. Update block value in IDB
      const existing = await appDB.blocks.get(blockId);
      if (existing) {
        await appDB.blocks.put({
          ...existing,
          value: data,
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      // Use the block's own parentId from IDB (authoritative — correct for
      // nested blocks). The caller passes the block's OWN parentId (from context)
      // as the fallback for when it isn't in IDB; the page (noteId) is the last
      // resort. SyncWatcher sends this as `parentId` in batch-update and the
      // server does $set: { parentId } — so it must be the block's real parent,
      // never the page's parent.
      const effectiveParentId = existing?.parentId ?? parentId ?? noteId;

      const payload: BlockChangesPayload = {
        noteId,
        workspaceId,
        parentId: effectiveParentId,
        creates: [],
        updates: [{ blockId, data }],
        deletes: [],
        move: null,
      };

      // 2. Enqueue sync operation
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "BLOCK_CHANGES",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateBlockContentMutation");
      await postWithAuth("/api/note/block/batch-update", {
        parentId: parentId ?? noteId,
        workspaceId,
        blocks: [{ _id: blockId, content: data }],
      });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE BLOCK FIELDS (title, icon, coverUrl, etc.)
// Replaces: blockContext.updateBlock() for page-level field updates
// ─────────────────────────────────────────────────────────────────

export interface UpdateBlockFieldsOptions {
  blockId: string;
  noteId: string;
  updates: Partial<Block>;
  workspaceId: string;
  parentId: string | null;
}

export interface UpdatePageSettingsOptions {
  blockId: string;
  workspaceId: string;
  settings: any;
}

/**
 * Updates pageSettings on a page block and enqueues PAGE_SETTINGS_UPDATE.
 * SyncWatcher routes this to the dedicated page-settings API endpoint.
 */
export async function updatePageSettingsMutation(
  options: UpdatePageSettingsOptions
): Promise<void> {
  const { blockId, workspaceId, settings } = options;
  const payload: PageSettingsUpdatePayload = { blockId, workspaceId, settings };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/blocks/update-page-settings", {
      blockId,
      settings,
    });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      const existing = await appDB.blocks.get(blockId);
      if (existing) {
        await appDB.blocks.put({
          ...existing,
          value: {
            ...(existing.value as any),
            pageSettings: settings,
          },
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "PAGE_SETTINGS_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updatePageSettingsMutation");
      await postWithAuth("/api/blocks/update-page-settings", {
        blockId,
        settings,
      });
      return;
    }
    throw err;
  }
}

/**
 * Updates any fields on a block (title, icon, coverUrl, pageType, etc.).
 * Used for page-level metadata edits — not rich text content.
 */
export async function updateBlockFieldsMutation(
  options: UpdateBlockFieldsOptions
): Promise<void> {
  const { blockId, noteId, updates, workspaceId, parentId } = options;

  // ── Direct API path ───────────────────────────────────────────────────────
  // Without IDB we cannot read existing block state to merge. Send `updates.value`
  // directly — it carries all the changed fields set by the caller.
  if (!isIDBAvailable()) {
    const content = (updates as any).value;
    if (content !== undefined) {
      await postWithAuth("/api/note/block/batch-update", {
        parentId,
        workspaceId,
        blocks: [{ _id: blockId, content }],
      });
    }
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      // 1. Merge updates into existing block
      const existing = await appDB.blocks.get(blockId);
      if (!existing) return;

      const updatedBlock = {
        ...existing,
        ...updates,
        _id: blockId,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      };
      await appDB.blocks.put(updatedBlock);

      // 2. Enqueue as a content update with the new merged value
      const payload: BlockChangesPayload = {
        noteId,
        workspaceId,
        parentId,
        creates: [],
        updates: [{ blockId, data: updatedBlock.value }],
        deletes: [],
        move: null,
      };
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "BLOCK_CHANGES",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateBlockFieldsMutation");
      const content = (updates as any).value;
      if (content !== undefined) {
        await postWithAuth("/api/note/block/batch-update", {
          parentId,
          workspaceId,
          blocks: [{ _id: blockId, content }],
        });
      }
      return;
    }
    throw err;
  }
}

/**
 * Caches a block's `comments` id list locally. No syncQueue op — the server
 * owns this field (comment create $addToSets it there).
 */
export async function updateBlockCommentsMutation(
  blockId: string,
  comments: string[],
): Promise<void> {
  if (!isIDBAvailable()) return;
  try {
    // Field patch, not get-then-put: that would rewrite the whole record and
    // could clobber block content saved in between.
    await appDB.blocks.update(blockId, { comments });
  } catch (err) {
    console.warn("[idb] Failed to mirror block comments:", err);
  }
}

// ─────────────────────────────────────────────────────────────────
// DELETE BLOCK
// Replaces: blockContext.removeBlock()
// ─────────────────────────────────────────────────────────────────

export interface DeleteBlockOptions {
  blockId: string;
  noteId: string;
  workspaceId: string;
  parentId: string | null;
}

/**
 * Soft-deletes a block locally (status = "deleted"), unlinks it from its
 * parent's blockIds, and enqueues a DELETE operation — all in one atomic
 * transaction. Self-sufficient: the parent-link removal lives here, so callers
 * (blockServices) only update context, never raw IDB.
 */
export async function deleteBlockMutation(options: DeleteBlockOptions): Promise<void> {
  const { blockId, noteId, workspaceId } = options;

  // BLOCK_DELETE routes to /api/note/block/delete/permanent-delete via SyncWatcher.
  const deletePayload: BlockDeletePayload = { blockId, workspaceId };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await deleteWithAuth("/api/note/block/delete/permanent-delete", {
      body: JSON.stringify(deletePayload),
    });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      // 1. Soft delete in IDB (and capture the block's own parent for unlinking).
      const existing = await appDB.blocks.get(blockId);
      const ownParentId = existing?.parentId ?? null;
      if (existing) {
        await appDB.blocks.put({
          ...existing,
          status: "deleted",
          deletedAt: new Date().toISOString(),
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      // 2. Unlink from the parent's blockIds so reconstruction won't reference a
      //    deleted child (ghost). The parent is read from IDB (authoritative).
      //    Workspace/workarea parents aren't in appDB.blocks → get() undefined → skipped.
      if (ownParentId) {
        const parent = await appDB.blocks.get(ownParentId);
        if (parent?.blockIds?.includes(blockId)) {
          await appDB.blocks.put({
            ...parent,
            blockIds: parent.blockIds.filter((id) => id !== blockId),
            isSync: 0 as 0,
            localUpdatedAt: Date.now(),
          });
        }
      }

      // 3. Enqueue BLOCK_DELETE so SyncWatcher sends to permanent-delete
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "BLOCK_DELETE",
        payload: deletePayload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId,
        workspaceId,
      });
    });

    await cascadeDatasourceDeleteInIDB({ rootBlockIds: [blockId], workspaceId });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("deleteBlockMutation");
      await deleteWithAuth("/api/note/block/delete/permanent-delete", {
        body: JSON.stringify(deletePayload),
      });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// DELETE TEMPLATE PAGE
// Dedicated path for per-datasource template pages so the normal delete
// mutation/endpoint stay untouched. Calls DELETE /api/note/block/datasource-template/delete
// (which pulls pageTemplateIds + clears defaultPageTemplateId server-side),
// then mirrors the same updates in IDB. Direct API call — template management
// is a rare online action, not a queued edit.
// ─────────────────────────────────────────────────────────────────

export interface DeleteTemplateOptions {
  blockId: string;
  datasourceId: string;
  workspaceId: string;
}

export async function deleteTemplateMutation(options: DeleteTemplateOptions): Promise<void> {
  const { blockId, datasourceId, workspaceId } = options;

  await deleteWithAuth("/api/note/block/datasource-template/delete", {
    body: JSON.stringify({ blockId, workspaceId }),
  });

  if (!isIDBAvailable()) return;
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.dataSources], async () => {
      const existing = await appDB.blocks.get(blockId);
      if (existing) {
        await appDB.blocks.put({
          ...existing,
          status: "deleted",
          deletedAt: new Date().toISOString(),
          isSync: 1 as 1, // server already deleted it — nothing to sync
          localUpdatedAt: Date.now(),
        });
      }
      const ds = await appDB.dataSources.get(datasourceId);
      if (ds) {
        const dsAny = ds as any;
        const update: any = {
          pageTemplateIds: (dsAny.pageTemplateIds ?? []).filter((id: string) => id !== blockId),
          localUpdatedAt: Date.now(),
        };
        if (dsAny.defaultPageTemplateId === blockId) {
          update.defaultPageTemplateId = undefined;
        }
        await appDB.dataSources.update(datasourceId, update);
      }
    });
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) handleQuotaError("deleteTemplateMutation");
    else throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// CREATE PAGE
// Used when adding a root or child page from the sidebar.
// Enqueues PAGE_CREATE — watcher routes this to /api/note/block/batch-create
// which handles permissions, parentTable, workareaId correctly.
// ─────────────────────────────────────────────────────────────────

export interface CreatePageOptions {
  block: Block;
  workspaceId: string;
  workareaId: string | null;
  parentId: string;                         // workspaceId, workAreaId, parent page _id, or parent block _id
  parentTable: ParentTable;                 // "workspace" | "workarea" | "page" | "block"
  insertAfterBlockID: string | null;        // ordering hint (null = first)
  isTemplate?: boolean;                     // true when the new page is a template
                                            // (forwarded as top-level field on /api/note/block/batch-create)
}

/**
 * Saves a new block to IDB and enqueues a BLOCK_CREATE op.
 * SyncWatcher will POST to /api/note/block/batch-create in the background.
 *
 * Dynamically updates the parent's blockIds in IDB for any parentTable whose
 * parent record lives in appDB.blocks (page, block). Workspace and workarea
 * parents are not stored in appDB.blocks so get() returns undefined for those —
 * the if (parentBlock) guard skips them safely without any explicit check.
 *
 * A duplicate guard (includes check) prevents double-appending the blockId when
 * applyContextUpdatesSync has already written it to IDB via Pass 4.
 */
export async function createBlockMutation(options: CreatePageOptions): Promise<void> {
  const { block, workspaceId, workareaId, parentId, parentTable, insertAfterBlockID, isTemplate } = options;

  const payload: PageCreatePayload = {
    parentId,
    workspaceId,
    workareaId,
    parentTable,
    blocks: [
      {
        _id: block._id,
        blockType: block.blockType,
        value: block.value,
        insertAfterBlockID,
      },
    ],
    ...(isTemplate ? { isTemplate: true } : {}),
  };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/note/block/batch-create", payload);
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.dataSources, appDB.syncQueue], async () => {
      // 1. Write new block to IDB with isSync: 0
      // CRITICAL: applyContextUpdatesSync may have already populated this block's blockIds
      // with reparented children. sendBlockChangesInternal passes blockIds: [], which
      // would destructively overwrite the carefully curated relationships.
      const existing = await appDB.blocks.get(block._id);
      const blockToSave = existing?.blockIds?.length && (!block.blockIds || block.blockIds.length === 0)
        ? { ...block, blockIds: existing.blockIds }
        : block;

      await appDB.blocks.put(toBlockRecord(blockToSave, 0));

      // 2. Update parent's blockIds in IDB — works for any parentTable whose parent
      //    is stored in appDB.blocks (page, block). Workspace/workarea parents are not
      //    in the store so get() returns undefined and this is skipped automatically.
      //    Duplicate guard: Pass 4 in applyContextUpdatesSync may have already written
      //    the correct blockIds — only append if the id isn't already present.
      const parentBlock = await appDB.blocks.get(parentId);
      if (parentBlock) {
        const existingBlockIds = parentBlock.blockIds ?? [];
        if (!existingBlockIds.includes(block._id)) {
          await appDB.blocks.put({
            ...parentBlock,
            blockIds: [...existingBlockIds, block._id],
            isSync: 0 as 0,
            localUpdatedAt: Date.now(),
          });
        }
      }

      // 2b. If the parent is a datasource (collection), update the appropriate
      //     array on the dataSource record. Templates go to pageTemplateIds;
      //     regular rows go to blockIds. Mirrors the server-side branch in
      //     batchCreateBlocks. Caller may have already optimistically updated
      //     this (e.g. addRootPage) — the includes() guard keeps it idempotent.
      if (parentTable === "collection") {
        const parentDs = await appDB.dataSources.get(parentId);
        if (parentDs) {
          const targetField = isTemplate ? "pageTemplateIds" : "blockIds";
          const existingIds = ((parentDs as any)[targetField] as string[] | undefined) ?? [];
          if (!existingIds.includes(block._id)) {
            await appDB.dataSources.update(parentId, {
              [targetField]: [...existingIds, block._id],
              isSync: 0 as 0,
              localUpdatedAt: Date.now(),
            } as any);
          }
        }
      }

      // 3. Enqueue BLOCK_CREATE op — routes to /api/note/block/batch-create
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "BLOCK_CREATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: block._id,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("createBlockMutation");
      await postWithAuth("/api/note/block/batch-create", payload);
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// ATOMIC SUBTREE CREATE
// Writes a whole set of new blocks (a nested subtree, or a flat batch)
// + their parent-link updates in ONE IndexedDB transaction, then enqueues
// one BLOCK_CREATE op per block (parent-first).
//
// Replaces the old split create path:
//   - applyContextUpdatesSync Pass 4 (eager raw write, no queue op), AND
//   - processCreationLayers → createBlockMutation per block (queued, lagging)
// which wrote each block TWICE, let the queued write lag ~1s behind typing,
// could overwrite newer text with a stale snapshot, and — because the only
// atomic writer (Pass 4) was fire-and-forget — could leave a half-linked
// subtree on an instant reload (the disappearing-content bug).
//
// Block VALUES come from the caller (read from context = latest), so a create
// can never clobber newer content. One transaction ⇒ reload sees the whole
// subtree or none of it — never an orphaned child.
// ─────────────────────────────────────────────────────────────────

export interface CreateBlocksInput {
  /**
   * New blocks in parent-first (document) order. `blockIds` is each block's
   * final child list as already computed by context — the mutation just mirrors
   * it to IDB (single source of truth = context).
   */
  blocks: Array<{
    _id: string;
    blockType: string;
    value: unknown;
    parentId: string;
    parentType: ParentTable;
    workspaceId: string;
    workareaId: string | null;
    blockIds: string[];
    previousBlockId: string | null; // → insertAfterBlockID in the BLOCK_CREATE op
  }>;
  /** Pre-existing parents (e.g. the page) whose blockIds gained a new child. */
  parentUpdates: Array<{ parentId: string; blockIds: string[] }>;
  workspaceId: string;
}

function batchCreatePayloadFor(b: CreateBlocksInput["blocks"][number]): PageCreatePayload {
  return {
    parentId: b.parentId,
    workspaceId: b.workspaceId,
    workareaId: b.workareaId ?? null,
    parentTable: b.parentType,
    blocks: [
      { _id: b._id, blockType: b.blockType, value: b.value, insertAfterBlockID: b.previousBlockId },
    ],
  } as PageCreatePayload;
}

/**
 * Stable parent-first sort: a block whose parent is also in this batch is placed
 * AFTER that parent. Guarantees the BLOCK_CREATE ops are sent parent-before-child
 * regardless of the caller's order (the IDB tree is already correct either way;
 * this just makes the server send-order correct by construction).
 */
function sortParentFirst(blocks: CreateBlocksInput["blocks"]): CreateBlocksInput["blocks"] {
  const byId = new Map(blocks.map((b) => [b._id, b]));
  const placed = new Set<string>();
  const visiting = new Set<string>();
  const ordered: CreateBlocksInput["blocks"] = [];
  const visit = (b: CreateBlocksInput["blocks"][number]): void => {
    if (placed.has(b._id) || visiting.has(b._id)) return;
    visiting.add(b._id);
    const parent = byId.get(b.parentId);
    if (parent) visit(parent);
    visiting.delete(b._id);
    placed.add(b._id);
    ordered.push(b);
  };
  for (const b of blocks) visit(b);
  return ordered;
}

export async function createBlocksMutation(input: CreateBlocksInput): Promise<void> {
  const { parentUpdates, workspaceId } = input;
  if (input.blocks.length === 0) return;

  // Parent-first so children are never created before their parents.
  const blocks = sortParentFirst(input.blocks);

  // ── Direct API path (IDB unavailable) ──────────────────────────────────────
  if (!isIDBAvailable()) {
    for (const b of blocks) {
      await postWithAuth("/api/note/block/batch-create", batchCreatePayloadFor(b));
    }
    return;
  }

  // ── IDB path: one atomic transaction for the whole subtree ─────────────────
  try {
    const baseTs = Date.now();
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      // 1. Write each new block row with the blockIds context already computed.
      for (const b of blocks) {
        await appDB.blocks.put(
          toBlockRecord(
            {
              _id: b._id,
              blockType: b.blockType,
              value: b.value,
              parentId: b.parentId,
              parentType: b.parentType,
              workspaceId: b.workspaceId,
              workareaId: b.workareaId ?? null,
              status: "alive",
              blockIds: b.blockIds ?? [],
            } as Block,
            0,
          ),
        );
      }

      // 2. Patch pre-existing parents' blockIds (e.g. the page now owning the
      //    new top-level block). New parents are written above with final blockIds.
      for (const pu of parentUpdates) {
        const parent = await appDB.blocks.get(pu.parentId);
        if (parent) {
          await appDB.blocks.put({
            ...parent,
            blockIds: pu.blockIds,
            isSync: 0 as 0,
            localUpdatedAt: Date.now(),
          });
        }
      }

      // 3. One BLOCK_CREATE op per block, in the given (parent-first) order.
      for (let i = 0; i < blocks.length; i++) {
        const b = blocks[i]!;
        await appDB.syncQueue.add({
          id: crypto.randomUUID(),
          operationType: "BLOCK_CREATE",
          payload: batchCreatePayloadFor(b),
          createdAt: baseTs + i,
          isSync: 0 as 0,
          attempts: 0,
          lastAttemptAt: null,
          syncError: null,
          noteId: b._id,
          workspaceId,
        });
      }
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("createBlocksMutation");
      for (const b of blocks) {
        await postWithAuth("/api/note/block/batch-create", batchCreatePayloadFor(b));
      }
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// BULK LOAD (initial hydration — no sync queue entry)
// Replaces: blockContext.loadBlocks() and upsertBlocks()
// ─────────────────────────────────────────────────────────────────

/**
 * Load blocks from the API into IDB.
 * Marks all as isSync: 1 — data already exists in MongoDB.
 * Does NOT add to syncQueue.
 *
 * If IDB is unavailable the blocks are already in context — skip silently.
 */
export async function loadBlocksMutation(blocks: Block[]): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    const records = blocks.map((b) => toBlockRecord(b, 1));
    await appDB.blocks.bulkPut(records);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("loadBlocksMutation");
      return; // No API fallback — data came FROM the API; context already has it
    }
    throw err;
  }
}

/**
 * Upsert blocks from the API (e.g., after a refresh or socket event).
 * Marks all as isSync: 1.
 * Does NOT add to syncQueue.
 *
 * If IDB is unavailable the blocks are already in context — skip silently.
 *
 * SAFE UPSERT: Blocks that already exist in IDB with isSync: 0 (user has
 * unsynced local edits) are NOT overwritten. The local version is newer than
 * what the server returned — overwriting it would cause the "stale content on
 * refresh" bug where rapidly-typed content disappears.
 *
 * @param blocks   The blocks to write.
 * @param pageId   Optional: the page these blocks belong to (editorKey).
 *                 When provided, updates the pageCache record and triggers
 *                 LRU eviction in the background. Safe to omit for non-page
 *                 contexts (collaborative updates, etc.).
 */
export async function upsertBlocksMutation(blocks: Block[], pageId?: string): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    // Filter out any block with PENDING unsynced local edits, derived from
    // the syncQueue (Step 3 — the per-row isSync flag is no longer maintained
    // by sync-acks, so it can't be trusted here). Those blocks are newer than
    // what the server returned — overwriting them would cause the "stale
    // content on refresh" bug where rapidly-typed content disappears.
    const blockIds = blocks.map((b) => b._id);
    const pendingIds = await getPendingBlockIds();

    // Build a set of block IDs that have unsynced local edits.
    const unsyncedIds = new Set<string>();
    for (const id of blockIds) {
      if (pendingIds.has(id)) unsyncedIds.add(id);
    }

    const safeRecords = blocks
      .filter((b) => !unsyncedIds.has(b._id))
      .map((b) => toBlockRecord(b, 1));
      console.log("safeRecords &&", safeRecords, unsyncedIds);

    if (safeRecords.length > 0) {
      await appDB.blocks.bulkPut(safeRecords);
    }

    if (unsyncedIds.size > 0) {
      console.log(
        `%c[upsertBlocksMutation] Preserved ${unsyncedIds.size} unsynced block(s) — server copy skipped`,
        "color: #60a5fa; font-weight: bold;",
        [...unsyncedIds]
      );
    }

    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("upsertBlocksMutation");
      return; // No API fallback — data came FROM the API; context already has it
    }
    throw err;
  }

  // Trigger pageCache update + LRU eviction lazily in the background.
  // Only when a pageId is provided (i.e. a full page fetch just completed).
  // Fire-and-forget — never awaited, never blocks the caller.
  if (pageId && blocks.length > 0) {
    const blockIds = blocks.map((b) => b._id);

    // The page block's updatedAt is the server's authoritative "last modified" stamp
    // for the whole document. Find it by matching _id === pageId, then convert the
    // ISO string to a ms timestamp for storage. This becomes lastKnownServerUpdatedAt
    // in pageCache and is compared against the lightweight meta API in Effect A.
    const pageBlock = blocks.find((b) => b._id === pageId);
    const rawUpdatedAt = pageBlock?.updatedAt;
    const serverUpdatedAt: number | undefined = rawUpdatedAt
      ? new Date(rawUpdatedAt).getTime()
      : undefined;

    touchPageCache(pageId, blockIds, serverUpdatedAt, pageBlock?.lastEditedBy)
      .then(() => runEviction(pageId))
      .catch((err) => console.warn("[IDB eviction] Background cleanup error:", err));
  }
}

// ─────────────────────────────────────────────────────────────────
// DRAG AND DROP (reorder / reparent any block in the editor or sidebar)
// Atomically updates the moved block in appDB.blocks using updatedBlockInfo
// from the payload, and enqueues the PAGE_MOVE op for SyncWatcher.
// SyncWatcher routes PAGE_MOVE → /api/note/block/drag-and-drop.
// ─────────────────────────────────────────────────────────────────

export interface DragAndDropOptions {
  payload: PageMovePayload;
  noteId: string;       // primary block being moved (used as syncQueue.noteId)
  workspaceId: string;
}

/**
 * LEAN drag-and-drop. Each moved block carries its destination (`new*` fields)
 * and `insertAfterBlockId`. We mirror the server surgically in IDB:
 *   1. read the block's CURRENT parent (from the block itself) and $pull it from
 *      that parent's children — only the moved id is removed;
 *   2. update the moved block's own parent metadata to the destination;
 *   3. $push the moved id into the new parent right after `insertAfterBlockId`
 *      (front if null, end if the anchor is gone).
 * Because only the moved id is ever touched, concurrently-added siblings can
 * never be dropped. Parent collection is resolved by parentType ("collection" →
 * dataSources, else blocks). Then a PAGE_MOVE op is enqueued for SyncWatcher.
 * All writes are in one transaction.
 */
export async function dragAndDropMutation(options: DragAndDropOptions): Promise<void> {
  const { payload, noteId, workspaceId } = options;
  // Invariant: a block can NEVER be its own parent (self-reference → cycle /
  // infinite traversal). Drop any such entry before it can corrupt a blockIds list.
  const movedBlocks = (
    payload.updatedBlockInfoArray && payload.updatedBlockInfoArray.length > 0
      ? payload.updatedBlockInfoArray
      : [payload.updatedBlockInfo]
  ).filter((m) => m && String(m.blockId) !== String(m.newParentId));
  if (movedBlocks.length === 0) return;

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    // workspaceId rides at the top level — the server needs it for the permission
    // check and for resolving workspace-root section lists.
    await postWithAuth("/api/note/block/drag-and-drop", { ...payload, workspaceId });
    return;
  }

  // Insert `blockId` into `arr` right after `insertAfterBlockId` (front if null,
  // end if the anchor isn't present). Removes any existing copy first so a
  // reorder can't duplicate. Only this one id is moved; the rest are preserved.
  const placeAfter = (arr: string[], blockId: string, insertAfterBlockId: string | null): string[] => {
    const base = arr.filter((id) => id !== blockId);
    if (insertAfterBlockId === null) return [blockId, ...base];
    const at = base.indexOf(insertAfterBlockId);
    return at >= 0 ? [...base.slice(0, at + 1), blockId, ...base.slice(at + 1)] : [...base, blockId];
  };
  // Pull `blockId` out of a parent's children, resolving the store by type.
  const removeFromParent = async (parentId: string, parentType: string | null, blockId: string) => {
    if (parentType === "collection") {
      const ds = await appDB.dataSources.get(parentId);
      if (ds) await appDB.dataSources.update(parentId, {
        blockIds: (Array.isArray(ds.blockIds) ? ds.blockIds : []).filter((id) => id !== blockId),
        localUpdatedAt: Date.now(),
      });
    } else {
      const p = await appDB.blocks.get(parentId);
      if (p) await appDB.blocks.update(parentId, {
        blockIds: (Array.isArray(p.blockIds) ? p.blockIds : []).filter((id) => id !== blockId),
        localUpdatedAt: Date.now(),
      });
    }
  };

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue, appDB.dataSources], async () => {
      for (const moved of movedBlocks) {
        const block = await appDB.blocks.get(moved.blockId);
        // Invariant: a block can't be positioned after itself — treat a self-anchor
        // as "front" (null) rather than letting it silently fall to the end.
        const anchor = moved.insertAfterBlockId === moved.blockId ? null : moved.insertAfterBlockId;

        // 1. Remove the block from its CURRENT parent (derived from the block).
        if (block?.parentId) {
          await removeFromParent(block.parentId, block.parentType ?? null, moved.blockId);
        }

        // 2. Update the moved block's own parent metadata to the destination.
        if (block) {
          await appDB.blocks.update(moved.blockId, {
            parentType: moved.newParentType as any,
            parentId: moved.newParentId,
            workareaId: moved.newWorkareaId,
            value: { ...(block.value as any), pageType: moved.newPageType },
          });
        }

        // 3. Insert into the NEW parent right after insertAfterBlockId.
        if (moved.newParentType === "collection") {
          const ds = await appDB.dataSources.get(moved.newParentId);
          if (ds) await appDB.dataSources.update(moved.newParentId, {
            blockIds: placeAfter(Array.isArray(ds.blockIds) ? ds.blockIds : [], moved.blockId, anchor),
            localUpdatedAt: Date.now(),
          });
        } else {
          const p = await appDB.blocks.get(moved.newParentId);
          if (p) await appDB.blocks.update(moved.newParentId, {
            blockIds: placeAfter(Array.isArray(p.blockIds) ? p.blockIds : [], moved.blockId, anchor),
            localUpdatedAt: Date.now(),
          });
        }
      }

      // 4. Enqueue PAGE_MOVE — SyncWatcher will POST to /api/note/block/drag-and-drop
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "PAGE_MOVE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("dragAndDropMutation");
      await postWithAuth("/api/note/block/drag-and-drop", { ...payload, workspaceId });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// MOVE PAGE (public ↔ private ↔ restricted, or child → root)
// Replaces: updateBlock(pageType) [Context] + postWithAuth(drag-and-drop)
// ─────────────────────────────────────────────────────────────────

export interface MovePageOptions {
  noteId: string;
  workspaceId: string;
  newPageType: "private" | "public" | "restricted" | "template" | "workarea";
  /** parentId of the page being moved. null if it is already a root page. */
  sourceParentId: string | null;
  /** parentType of the page being moved. When "page", removes noteId from parent blockIds. */
  sourceParentType: string;
  /** Full drag-and-drop payload — stored verbatim in syncQueue for SyncWatcher. */
  dragAndDropPayload: PageMovePayload;
  /**
   * Destination parent. Defaults to the workspace root, which is where the
   * private/public/template buckets live. Work areas are the exception: the
   * page re-parents onto the work area itself, so those callers pass
   * "workarea" + the work area id.
   */
  newParentType?: ParentTable;
  newParentId?: string;
  /**
   * Written only when provided, so existing workspace-root callers keep their
   * current behaviour of leaving workareaId untouched.
   */
  newWorkareaId?: string | null;
}

/**
 * Moves a page to a new section (private / public / restricted) in IDB and
 * enqueues a PAGE_MOVE op. SyncWatcher will POST to /api/note/block/drag-and-drop.
 *
 * In one atomic transaction:
 *   1. Updates the moved block: value.pageType, parentType → "workspace", parentId → workspaceId
 *   2. If the page was a child page (sourceParentType === "page"), removes noteId from
 *      the source parent's blockIds array.
 *   3. Enqueues PAGE_MOVE with the full drag-and-drop payload.
 */
export async function movePageMutation(options: MovePageOptions): Promise<void> {
  const {
    noteId,
    workspaceId,
    newPageType,
    sourceParentId,
    sourceParentType,
    dragAndDropPayload,
    newParentType = "workspace",
    newParentId = workspaceId,
    newWorkareaId,
  } = options;

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/note/block/drag-and-drop", { ...dragAndDropPayload, workspaceId });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      // 1. Update the moved block's pageType and re-root it under the workspace
      const block = await appDB.blocks.get(noteId);
      if (block) {
        await appDB.blocks.put({
          ...block,
          parentType: newParentType,
          parentId: newParentId,
          ...(newWorkareaId !== undefined ? { workareaId: newWorkareaId } : {}),
          value: {
            ...(block.value as any),
            pageType: newPageType,
            // isTemplate tracks pageType (mirrors the server) for moves into/out of Templates.
            isTemplate: newPageType === "template",
          },
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      // 2. If it was a child page, remove noteId from the source parent's blockIds
      if (sourceParentType === "page" && sourceParentId) {
        const parentBlock = await appDB.blocks.get(sourceParentId);
        if (parentBlock) {
          const updatedBlockIds = (parentBlock.blockIds ?? []).filter((id) => id !== noteId);
          await appDB.blocks.put({
            ...parentBlock,
            blockIds: updatedBlockIds,
            isSync: 0 as 0,
            localUpdatedAt: Date.now(),
          });
        }
      }

      // 3. Enqueue PAGE_MOVE — SyncWatcher routes to /api/note/block/drag-and-drop
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "PAGE_MOVE",
        payload: dragAndDropPayload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("movePageMutation");
      await postWithAuth("/api/note/block/drag-and-drop", { ...dragAndDropPayload, workspaceId });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// TRASH PAGE  (soft-delete → trash, NOT permanent delete)
// Routes to /api/note/block/delete/trash via SyncWatcher BLOCK_TRASH.
// The block stays in IDB with status:"deleted" so the TrashPanel can
// still read it. Sidebar removal is handled by the caller via
// removePageFromAllMutation(blockId, workspaceId, true) BEFORE this call.
// ─────────────────────────────────────────────────────────────────

export interface TrashPageOptions {
  blockId: string;
  workspaceId: string;
  parentId: string;
  parentType: string; // "page" → remove blockId from parent's blockIds; otherwise skip
}

export async function trashPageMutation(options: TrashPageOptions): Promise<void> {
  const { blockId, workspaceId, parentId, parentType } = options;
  const payload: BlockDeletePayload = { blockId, workspaceId };

  // Read current user from localStorage so the banner can show "X moved to Trash"
  let deletedBy: { userId: string; userName: string; userEmail: string } | undefined;
  try {
    const u = JSON.parse(localStorage.getItem("auth_user") || "{}");
    if (u?.email) {
      deletedBy = { userId: u.id || "", userName: u.name || "", userEmail: u.email };
    }
  } catch {}
  const deletedAt = new Date().toISOString();

  if (!isIDBAvailable()) {
    await deleteWithAuth("/api/note/block/delete/trash", {
      body: JSON.stringify(payload),
    });
    return;
  }

  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      // 1. Soft-delete the block (mark as trashed)
      const existing = await appDB.blocks.get(blockId);
      if (existing) {
        await appDB.blocks.put({
          ...existing,
          status: "deleted",
          ...(deletedBy ? { deletedBy } : {}),
          deletedAt,
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      // 2. If child page, remove blockId from parent's blockIds array
      if (parentType === "page") {
        const parentBlock = await appDB.blocks.get(parentId);
        if (parentBlock) {
          const updatedBlockIds = (parentBlock.blockIds ?? []).filter((id) => id !== blockId);
          await appDB.blocks.put({
            ...parentBlock,
            blockIds: updatedBlockIds,
            isSync: 0 as 0,
            localUpdatedAt: Date.now(),
          });
        }
      }

      // 3. Enqueue BLOCK_TRASH — SyncWatcher routes to /api/note/block/delete/trash
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "BLOCK_TRASH",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("trashPageMutation");
      await deleteWithAuth("/api/note/block/delete/trash", {
        body: JSON.stringify(payload),
      });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// RESTORE PAGE FROM TRASH
// Routes to /api/note/block/delete/restore via SyncWatcher BLOCK_RESTORE.
// Sets status:"alive" in IDB. If parentType === "page" (child page), also
// re-inserts the blockId into the parent's blockIds array. For root pages
// the caller is responsible for calling addPrivatePageMutation /
// addPublicPageMutation to restore sidebar order.
// ─────────────────────────────────────────────────────────────────

export interface RestorePageOptions {
  blockId: string;
  workspaceId: string;
  parentId: string;
  parentType: string; // "page" → re-insert into parent blockIds; otherwise skip
}

export async function restorePageMutation(options: RestorePageOptions): Promise<void> {
  const { blockId, workspaceId, parentId, parentType } = options;
  const payload: BlockDeletePayload = { blockId, workspaceId };

  if (!isIDBAvailable()) {
    await postWithAuth("/api/note/block/delete/restore", {
      blockId,
      workspaceId,
    });
    return;
  }

  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.syncQueue], async () => {
      // 1. Mark the block as alive again
      const existing = await appDB.blocks.get(blockId);
      if (existing) {
        await appDB.blocks.put({
          ...existing,
          status: "alive",
          deletedBy: undefined,
          deletedAt: undefined,
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      // 2. If child page, re-add blockId to parent's blockIds array
      if (parentType === "page") {
        const parentBlock = await appDB.blocks.get(parentId);
        if (parentBlock) {
          const currentBlockIds = parentBlock.blockIds ?? [];
          if (!currentBlockIds.includes(blockId)) {
            await appDB.blocks.put({
              ...parentBlock,
              blockIds: [...currentBlockIds, blockId],
              isSync: 0 as 0,
              localUpdatedAt: Date.now(),
            });
          }
        }
      }

      // 3. Enqueue BLOCK_RESTORE — SyncWatcher routes to /api/note/block/delete/restore
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "BLOCK_RESTORE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("restorePageMutation");
      await postWithAuth("/api/note/block/delete/restore", {
        blockId,
        workspaceId,
      });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// DELETE PAGE
// Replaces: direct deleteWithAuth('/api/note/block/delete/permanent-delete')
// ─────────────────────────────────────────────────────────────────

export interface DeletePageOptions {
  blockId: string;
  workspaceId: string;
  parentId: string;
  parentType: string;   // "page" → update parent blockIds; otherwise skip
}

/**
 * Soft-deletes a page block in IDB and enqueues a BLOCK_DELETE op.
 * SyncWatcher will call /api/note/block/delete/permanent-delete in the background.
 *
 * When parentType === "page" (child page), also removes the blockId from the
 * parent block's blockIds array in the same atomic transaction.
 */
export async function deletePageMutation(options: DeletePageOptions): Promise<void> {
  const { blockId, workspaceId, parentId, parentType } = options;

  const payload: BlockDeletePayload = { blockId, workspaceId };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await deleteWithAuth("/api/note/block/delete/permanent-delete", {
      body: JSON.stringify(payload),
    });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.blocks, appDB.dataSources, appDB.syncQueue], async () => {
      // 1. Soft-delete the block
      const existing = await appDB.blocks.get(blockId);
      if (existing) {
        await appDB.blocks.put({
          ...existing,
          status: "deleted",
          deletedAt: new Date().toISOString(),
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      // 2. Remove blockId from its parent's blockIds — a page parent lives in
      // the blocks store, a datasource row's collection parent in dataSources.
      if (parentType === "page") {
        const parentBlock = await appDB.blocks.get(parentId);
        if (parentBlock) {
          const updatedBlockIds = (parentBlock.blockIds ?? []).filter((id) => id !== blockId);
          await appDB.blocks.put({
            ...parentBlock,
            blockIds: updatedBlockIds,
            isSync: 0 as 0,
            localUpdatedAt: Date.now(),
          });
        }
      } else if (parentType === "collection") {
        const ds = await appDB.dataSources.get(parentId);
        if (ds) {
          await appDB.dataSources.update(parentId, {
            blockIds: (Array.isArray(ds.blockIds) ? ds.blockIds : []).filter((id) => id !== blockId),
            localUpdatedAt: Date.now(),
          });
        }
      }

      // 3. Enqueue BLOCK_DELETE — SyncWatcher routes to permanent-delete API
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "BLOCK_DELETE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: blockId,
        workspaceId,
      });
    });

    await cascadeDatasourceDeleteInIDB({ rootBlockIds: [blockId], workspaceId });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("deletePageMutation");
      await deleteWithAuth("/api/note/block/delete/permanent-delete", {
        body: JSON.stringify(payload),
      });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// INSTANTIATE TEMPLATE ROW (server-side clone)
// One POST to /api/note/block/datasource-template/instantiate: the server creates the
// row through the shared batchCreateBlocks (fresh unique id, permissions,
// blockIds splice) and deep-clones the template's content under it. The
// response carries every created block + any cloned nested datasources, so
// the client hydrates in a single round trip. Online-only — callers fall
// back to a blank row when the request fails.
// ─────────────────────────────────────────────────────────────────

export interface InstantiateTemplateRowOptions {
  /** The template's root block (already in context/IDB via the dropdown). */
  templateBlock: Block;
  datasourceId: string;
  workspaceId: string;
  /** Row id after which the new root is inserted (null → FIRST position, matching batch-create). */
  insertAfterBlockId?: string | null;
  /** The datasource's unique-id property — stripped so the server re-stamps. */
  uniqueIdPropertyId?: string;
  /** View-derived property values (filter/group) — win over template values. */
  rootPropertyOverrides?: Record<string, any>;
  /** Title typed by the user (beats the template title only when non-empty). */
  initialTitle?: string;
  /** Reuse a caller-generated id so optimistic UI rows match the server row. */
  preGeneratedRowId?: string;
  /** Replaces "Person duplicating page" sentinel entries in person values. */
  currentUser?: DuplicatorUser | null;
}

export interface InstantiateTemplateRowResult {
  rootBlock: Block;
  blocks: Block[];
  dataSources: DatabaseSource[];
}

/**
 * Row shell value = template value minus template-only fields, plus overrides.
 * Exported so callers can render an optimistic shell (row visible instantly,
 * editor shows loading) with the exact value the server will persist.
 */
export function buildTemplateRowValue(
  templateBlock: Block,
  opts: {
    uniqueIdPropertyId?: string;
    rootPropertyOverrides?: Record<string, any>;
    initialTitle?: string;
    currentUser?: DuplicatorUser | null;
  } = {},
): any {
  const rowValue = JSON.parse(JSON.stringify(templateBlock.value ?? {}));
  delete rowValue.isTemplate;
  delete rowValue.uniqueId;
  if (opts.uniqueIdPropertyId && rowValue.databaseProperties) {
    delete rowValue.databaseProperties[opts.uniqueIdPropertyId];
  }
  if (opts.rootPropertyOverrides && Object.keys(opts.rootPropertyOverrides).length > 0) {
    rowValue.databaseProperties = {
      ...(rowValue.databaseProperties ?? {}),
      ...opts.rootPropertyOverrides,
    };
  }
  if (opts.initialTitle) rowValue.title = opts.initialTitle;
  resolveTemplateDuplicator(rowValue.databaseProperties, opts.currentUser ?? null);
  return rowValue;
}

/**
 * Merge the server-created row into the locally-edited shell. The user can
 * edit title/properties while the instantiate request is in flight — those
 * local edits must survive the response. Local value fields win; the server
 * owns the unique id and the children list (cloned content).
 */
export function mergeInstantiatedRootBlock(
  serverRoot: Block,
  local: Block | undefined,
  uniqueIdPropertyId?: string,
): Block {
  if (!local) return serverRoot;
  const sv: any = serverRoot.value ?? {};
  const lv: any = local.value ?? {};
  const mergedValue: any = { ...sv, ...lv };
  if (sv.uniqueId !== undefined) mergedValue.uniqueId = sv.uniqueId;
  mergedValue.databaseProperties = {
    ...(sv.databaseProperties ?? {}),
    ...(lv.databaseProperties ?? {}),
    ...(uniqueIdPropertyId && sv.databaseProperties?.[uniqueIdPropertyId] !== undefined
      ? { [uniqueIdPropertyId]: sv.databaseProperties[uniqueIdPropertyId] }
      : {}),
  };
  return { ...serverRoot, value: mergedValue };
}

/**
 * Row ids whose instantiate request is in flight. Any UI opening one of these
 * rows (peek re-open, row click from another view) should show its content
 * loading state instead of rendering the shell's empty document.
 */
const pendingInstantiations = new Set<string>();

export function isInstantiationPending(rowId: string): boolean {
  return pendingInstantiations.has(rowId);
}

export async function instantiateTemplateRowMutation(
  options: InstantiateTemplateRowOptions,
): Promise<InstantiateTemplateRowResult> {
  const {
    templateBlock,
    datasourceId,
    workspaceId,
    insertAfterBlockId = null,
    uniqueIdPropertyId,
    rootPropertyOverrides,
    initialTitle,
    preGeneratedRowId,
    currentUser,
  } = options;

  const rowId = preGeneratedRowId ?? new ObjectId().toString();
  pendingInstantiations.add(rowId);
  try {
    const rowValue = buildTemplateRowValue(templateBlock, {
      uniqueIdPropertyId,
      rootPropertyOverrides,
      initialTitle,
      currentUser,
    });

    const res = await postWithAuth("/api/note/block/datasource-template/instantiate", {
      templateId: templateBlock._id,
      datasourceId,
      workspaceId,
      rowId,
      rowValue,
      insertAfterBlockId,
    });

    if (!res?.success || !res.rootBlock) {
      const detail =
        res?.status || res?.message
          ? ` (${[res?.status, res?.message].filter(Boolean).join(": ")})`
          : "";
      throw new Error(`Template instantiation failed${detail}`);
    }

    const rootBlock = res.rootBlock as Block;
    const blocks = (res.blocks ?? []) as Block[];
    const dataSources = (res.dataSources ?? []) as DatabaseSource[];

    // Merge against the IDB record (authoritative, transaction-serialized):
    // prefer the local value (shell, or an in-flight edit), graft the server's
    // unique id + children. isSync:0 is kept when a pending edit exists.
    let finalRoot = rootBlock;
    if (isIDBAvailable()) {
      try {
        await appDB.transaction("rw", [appDB.blocks, appDB.dataSources], async () => {
          const existingRoot = await appDB.blocks.get(rootBlock._id);
          const localPlain = existingRoot
            ? (() => {
                const { isSync, localUpdatedAt, syncAttempts, syncError, ...rest } =
                  existingRoot as any;
                return rest as Block;
              })()
            : undefined;
          finalRoot = mergeInstantiatedRootBlock(rootBlock, localPlain, uniqueIdPropertyId);
          const keepSync: 0 | 1 = existingRoot?.isSync === 0 ? 0 : 1;
          await appDB.blocks.put({ ...toBlockRecord(finalRoot, keepSync), localUpdatedAt: Date.now() });
          await appDB.blocks.bulkPut(blocks.map((b) => toBlockRecord(b, 1)));
          for (const ds of dataSources) {
            await appDB.dataSources.put(toDataSourceRecord(ds, workspaceId, 1));
          }
          const parentDs = await appDB.dataSources.get(datasourceId);
          if (parentDs) {
            const existing = Array.isArray(parentDs.blockIds) ? parentDs.blockIds : [];
            if (!existing.includes(rootBlock._id)) {
              await appDB.dataSources.update(datasourceId, {
                blockIds: [...existing, rootBlock._id],
                localUpdatedAt: Date.now(),
              } as any);
            }
          }
        });
        emitIDBChange();
        // Record the page-cache timestamp so the peek's loadContent trusts the
        // IDB we just hydrated instead of re-fetching the same content.
        const rawUpdatedAt = rootBlock?.updatedAt;
        touchPageCache(
          rootBlock._id,
          [rootBlock._id, ...blocks.map((b) => b._id)],
          rawUpdatedAt ? new Date(rawUpdatedAt).getTime() : undefined,
          rootBlock?.lastEditedBy,
        )
          .then(() => runEviction(rootBlock._id))
          .catch(() => {});
      } catch (err: any) {
        if (isQuotaError(err)) handleQuotaError("instantiateTemplateRowMutation");
        else throw err;
      }
    }

    return { rootBlock: finalRoot, blocks, dataSources };
  } finally {
    pendingInstantiations.delete(rowId);
  }
}
